// ============================================================================
// A file share's page at /s/<secret> (docs/areas/files.md), inside the plain
// share page (views/Shared.tsx): no device, no sync, no shell. The secret is
// the bearer; a password earns an unlock token kept for this tab only
// (sessionStorage); every view mints its own short presigned URL. A folder
// lists its contents and opens its files one at a time (downloads are per
// file: no zip). A file request is a picker and progress, never the folder.
// ============================================================================

import { useEffect, useState } from 'react'
import { ArrowLeft, Download, Folder, Lock, Upload } from 'lucide-react'
import { formatSize, typeLabel } from '@/lib/files/types'
import { postForm } from '@/lib/files/upload'
import { Button, cx } from '@/components/ui'
import { Phrases } from '@/components/Phrases'
import { FileViewer } from './Viewer'

export interface ShareEntry {
  id: string
  name: string
  folder?: boolean
  size?: number
  content_type?: string
  thumb?: boolean
  updated_at: string
}

export interface FileShareInfo {
  mode: 'view' | 'drop'
  owner?: string
  intro?: string
  locked?: boolean
  view_only?: boolean
  downloads_left?: number
  root?: ShareEntry
  entries?: ShareEntry[]
  drop_max_bytes?: number
  drops_left?: number
}

const unlockKey = (secret: string) => `share-unlock:${secret.slice(0, 12)}`
const readUnlock = (secret: string): string | null => {
  try {
    return sessionStorage.getItem(unlockKey(secret))
  } catch {
    return null
  }
}

const reasonOf = async (res: Response): Promise<string> => {
  try {
    return String(((await res.json()) as { error?: string }).error ?? res.status)
  } catch {
    return String(res.status)
  }
}

export const FileSharePage = ({ secret, initial }: { secret: string; initial: FileShareInfo }) => {
  const [info, setInfo] = useState(initial)
  const [unlock, setUnlock] = useState(() => readUnlock(secret))
  const [error, setError] = useState<string | null>(null)
  const headers = (json = false): Record<string, string> => ({
    Authorization: `Bearer ${secret}`,
    ...(unlock ? { 'X-Share-Unlock': unlock } : {}),
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  })
  const refresh = async (tok: string | null) => {
    const res = await fetch('/api/share', {
      headers: { Authorization: `Bearer ${secret}`, ...(tok ? { 'X-Share-Unlock': tok } : {}) },
    })
    if (!res.ok) throw new Error(await reasonOf(res))
    const body = (await res.json()) as { files?: FileShareInfo }
    if (body.files) setInfo(body.files)
  }
  // a tab that unlocked this link before keeps it open after a reload
  useEffect(() => {
    const tok = readUnlock(secret)
    if (!initial.locked || !tok) return
    fetch('/api/share', { headers: { Authorization: `Bearer ${secret}`, 'X-Share-Unlock': tok } })
      .then(async (res) => {
        if (!res.ok) return
        const body = (await res.json()) as { files?: FileShareInfo }
        if (body.files) setInfo(body.files)
      })
      .catch(() => {})
  }, [secret, initial.locked])
  const getUrl = async (id: string, variant: 'view' | 'download' | 'thumb') => {
    const res = await fetch('/api/share/files/url', {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({ id, variant }),
    })
    if (!res.ok) throw new Error(await reasonOf(res))
    return ((await res.json()) as { url: string }).url
  }
  const download = async (id: string) => {
    setError(null)
    try {
      const url = await getUrl(id, 'download')
      window.location.assign(url)
      if (info.downloads_left !== undefined)
        setInfo((i) => ({ ...i, downloads_left: Math.max(0, (i.downloads_left ?? 1) - 1) }))
    } catch (e) {
      setError(`Could not download: ${(e as Error).message}`)
    }
  }
  const owner = info.owner || 'Someone'

  if (info.mode === 'drop') return <DropPage secret={secret} info={info} owner={owner} />

  if (info.locked)
    return (
      <PasswordForm
        secret={secret}
        onUnlocked={async (tok) => {
          try {
            sessionStorage.setItem(unlockKey(secret), tok)
          } catch {
            /* private mode: unlocked for this page only */
          }
          setUnlock(tok)
          await refresh(tok)
        }}
      />
    )
  if (!info.root) return <p className="text-sm text-faint">Nothing is shared on this link any more.</p>
  const canDownload = !info.view_only && info.downloads_left !== 0
  return (
    <div data-testid="file-share">
      {info.root.folder ? (
        <FolderShare
          root={info.root}
          entries={info.entries ?? []}
          headers={headers}
          getUrl={getUrl}
          canDownload={canDownload}
          onDownload={(id) => void download(id)}
        />
      ) : (
        <OneFile
          entry={info.root}
          getUrl={getUrl}
          canDownload={canDownload}
          onDownload={(id) => void download(id)}
        />
      )}
      {error && <p className="text-sm text-danger mt-3">{error}</p>}
      <ShareFoot info={info} owner={owner} />
    </div>
  )
}

const ShareFoot = ({ info, owner }: { info: FileShareInfo; owner: string }) => (
  <div className="mt-6 text-xs text-faint space-y-1">
    {info.view_only && (
      <p data-testid="file-share-viewonly">
        {owner} shared this to look at, not to keep, so there is no Download. (A screen can always be saved;
        this only asks.)
      </p>
    )}
    {info.downloads_left !== undefined && (
      <p>
        {info.downloads_left === 0
          ? 'This link has had all its downloads.'
          : `${info.downloads_left} ${info.downloads_left === 1 ? 'download' : 'downloads'} left on this link.`}
      </p>
    )}
  </div>
)

const PasswordForm = ({
  secret,
  onUnlocked,
}: {
  secret: string
  onUnlocked: (tok: string) => Promise<void>
}) => {
  const [pw, setPw] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/share/unlock', {
        method: 'POST',
        headers: { Authorization: `Bearer ${secret}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      })
      if (res.status === 403) throw new Error('That is not the password.')
      if (res.status === 429) throw new Error('Too many tries. Wait a minute or two and try again.')
      if (!res.ok) throw new Error(await reasonOf(res))
      await onUnlocked(((await res.json()) as { unlock: string }).unlock)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (pw) void submit()
      }}
      data-testid="file-share-password"
    >
      <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight mb-2">
        <Lock size={22} className="text-muted" aria-hidden /> This needs a password
      </h1>
      <p className="text-[15px] text-muted mb-4">Whoever shared it will have told you what it is.</p>
      <div className="flex gap-2 rounded-full bg-panel border border-line p-1.5">
        <input
          type="password"
          autoFocus
          autoComplete="current-password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Password"
          className="bare-input flex-1 min-w-0 px-3"
          aria-label="Password"
          data-testid="file-share-password-input"
        />
        <Button variant="primary" type="submit" disabled={busy || !pw} className="rounded-full">
          {busy ? 'Opening…' : 'Open'}
        </Button>
      </div>
      {error && (
        <p className="text-sm text-danger mt-3" role="alert" data-testid="file-share-password-error">
          {error}
        </p>
      )}
    </form>
  )
}

const OneFile = ({
  entry,
  getUrl,
  canDownload,
  onDownload,
}: {
  entry: ShareEntry
  getUrl: (id: string, v: 'view' | 'download') => Promise<string>
  canDownload: boolean
  onDownload: (id: string) => void
}) => (
  <section>
    <h1 className="text-2xl font-semibold tracking-tight break-all mb-1" data-testid="file-share-name">
      {entry.name}
    </h1>
    <Phrases
      className="text-[13px] text-muted mb-3"
      parts={[typeLabel(entry.name, entry.content_type), formatSize(entry.size)]}
    />
    {canDownload && (
      <Button
        variant="primary"
        className="mb-4"
        onClick={() => onDownload(entry.id)}
        data-testid="file-share-download"
      >
        <Download size={16} aria-hidden /> Download
      </Button>
    )}
    <FileViewer
      file={{ id: entry.id, name: entry.name, size: entry.size, contentType: entry.content_type }}
      getUrl={(v) => getUrl(entry.id, v)}
      onDownload={canDownload ? () => onDownload(entry.id) : undefined}
    />
  </section>
)

const FolderShare = ({
  root,
  entries: rootEntries,
  headers,
  getUrl,
  canDownload,
  onDownload,
}: {
  root: ShareEntry
  entries: ShareEntry[]
  headers: () => Record<string, string>
  getUrl: (id: string, v: 'view' | 'download' | 'thumb') => Promise<string>
  canDownload: boolean
  onDownload: (id: string) => void
}) => {
  const [stack, setStack] = useState<{ folder: ShareEntry; entries: ShareEntry[] }[]>([])
  const [open, setOpen] = useState<ShareEntry | null>(null)
  const [error, setError] = useState<string | null>(null)
  const here = stack.length ? stack[stack.length - 1]! : { folder: root, entries: rootEntries }
  const enter = async (f: ShareEntry) => {
    setError(null)
    const res = await fetch(`/api/share/files?folder=${encodeURIComponent(f.id)}`, { headers: headers() })
    if (!res.ok) return setError(`Could not open it: ${await reasonOf(res)}`)
    const body = (await res.json()) as { entries: ShareEntry[] }
    setStack((s) => [...s, { folder: f, entries: body.entries }])
  }
  if (open)
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(null)}
          className="inline-flex items-center gap-1 min-h-11 text-sm text-muted mb-1"
        >
          <ArrowLeft size={16} aria-hidden /> {here.folder.name}
        </button>
        <OneFile entry={open} getUrl={getUrl} canDownload={canDownload} onDownload={onDownload} />
      </>
    )
  return (
    <section data-testid="file-share-folder">
      {stack.length > 0 && (
        <button
          type="button"
          onClick={() => setStack((s) => s.slice(0, -1))}
          className="inline-flex items-center gap-1 min-h-11 text-sm text-muted mb-1"
        >
          <ArrowLeft size={16} aria-hidden />{' '}
          {stack.length > 1 ? stack[stack.length - 2]!.folder.name : root.name}
        </button>
      )}
      <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight mb-4 break-all">
        <Folder size={22} className="text-muted shrink-0" aria-hidden /> {here.folder.name}
      </h1>
      {here.entries.length === 0 ? (
        <p className="text-sm text-faint">This folder is empty.</p>
      ) : (
        <ul className="group-list">
          {here.entries.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => (e.folder ? void enter(e) : setOpen(e))}
                className="w-full text-left px-3.5 py-2.5 flex items-center gap-3"
                data-testid="file-share-entry"
              >
                {e.folder && <Folder size={18} className="text-accent shrink-0" aria-hidden />}
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] truncate">{e.name}</span>
                  {!e.folder && (
                    <Phrases
                      className="text-xs text-faint"
                      parts={[typeLabel(e.name, e.content_type), formatSize(e.size)]}
                    />
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-sm text-danger mt-3">{error}</p>}
      {!canDownload ? null : (
        <p className="text-xs text-faint mt-3">
          Open a file to download it. Folders download one file at a time.
        </p>
      )}
    </section>
  )
}

type DropRow = {
  name: string
  size: number
  sent: number
  state: 'sending' | 'done' | 'failed'
  error?: string
}

const DropPage = ({ secret, info, owner }: { secret: string; info: FileShareInfo; owner: string }) => {
  const [rows, setRows] = useState<DropRow[]>([])
  const [left, setLeft] = useState(info.drops_left)
  const max = info.drop_max_bytes ?? 0
  const set = (i: number, patch: Partial<DropRow>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const send = async (file: File, i: number) => {
    const auth = { Authorization: `Bearer ${secret}`, 'Content-Type': 'application/json' }
    try {
      if (max && file.size > max) throw new Error(`bigger than the ${formatSize(max)} this link takes`)
      const start = await fetch('/api/share/drop', {
        method: 'POST',
        headers: auth,
        body: JSON.stringify({ name: file.name, size: file.size, content_type: file.type }),
      })
      if (!start.ok) throw new Error(await reasonOf(start))
      const { id, url, fields } = (await start.json()) as {
        id: string
        url: string
        fields: Record<string, string>
      }
      await postForm(url, fields, file, (sent) => set(i, { sent }))
      const done = await fetch(`/api/share/drop/${id}/done`, { method: 'POST', headers: auth })
      if (!done.ok) throw new Error(await reasonOf(done))
      set(i, { state: 'done', sent: file.size })
      setLeft((l) => (l === undefined ? l : Math.max(0, l - 1)))
    } catch (e) {
      set(i, { state: 'failed', error: (e as Error).message })
    }
  }
  const pick = (list: FileList | null) => {
    const files = list ? Array.from(list) : []
    const base = rows.length
    setRows((rs) => [
      ...rs,
      ...files.map((f) => ({ name: f.name, size: f.size, sent: 0, state: 'sending' as const })),
    ])
    // one at a time: a phone on a slow line sends each file whole before the next
    void files.reduce((p, f, k) => p.then(() => send(f, base + k)), Promise.resolve())
  }
  const full = left === 0
  return (
    <section data-testid="file-share-drop">
      <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight mb-2">
        <Upload size={22} className="text-muted" aria-hidden /> Send {owner} files
      </h1>
      <p className="text-[15px] text-muted mb-4">{info.intro || `Only ${owner} sees what you send.`}</p>
      <Phrases
        className="text-xs text-faint mb-4"
        parts={[
          max ? `Up to ${formatSize(max)} a file` : '',
          left !== undefined && `${left} more ${left === 1 ? 'file' : 'files'}`,
        ]}
      />
      <label
        className={cx(
          'flex flex-col items-center justify-center gap-2 rounded-[var(--radius-card)] border-2 border-dashed border-line px-4 py-10 text-center',
          full ? 'opacity-50' : 'cursor-pointer hover:border-line-strong',
        )}
      >
        <Upload size={26} className="text-muted" aria-hidden />
        <span className="text-[15px]">{full ? 'This link has all the files it takes' : 'Choose files'}</span>
        <input
          type="file"
          multiple
          hidden
          disabled={full}
          onChange={(e) => pick(e.target.files)}
          data-testid="file-share-drop-input"
        />
      </label>
      {rows.length > 0 && (
        <ul className="group-list mt-4">
          {rows.map((r, i) => (
            <li key={i} className="px-3.5 py-2.5 text-[14px]">
              <div className="flex items-center gap-2">
                <span className="flex-1 min-w-0 truncate">{r.name}</span>
                <span className="text-xs text-faint shrink-0">
                  {r.state === 'done'
                    ? 'Sent'
                    : r.state === 'failed'
                      ? 'Not sent'
                      : `${Math.round((r.sent / (r.size || 1)) * 100)}%`}
                </span>
              </div>
              {r.error && <p className="text-xs text-faint mt-0.5">{r.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
